/**
 * The streak callout — the word, not the badge.
 *
 * ## What this replaced, and why
 *
 * This file used to draw a bevelled plate: a ray fan, a glow pool, up to three
 * shock rings, a spark burst, a gradient plate over a drop shadow, a bevel
 * wash, a masked specular sweep, and type in a three-stop gradient flanked by
 * chevrons. Eight layers. The client's word for it was "cheesy", and four of
 * the causes turned out to be measurable rather than matters of taste:
 *
 * 1. **Overclaiming.** The plate was `CARD.width * 3.1` x `CARD.height * 0.86`
 *    = 641 x 249 design units, with a ray fan 1532 units across. That is 33% of
 *    the landscape width and **75% of the portrait width**, with a fan 1.8x the
 *    entire width of a portrait screen — for a `+500` on a score that runs into
 *    the tens of thousands, five times a round. A celebration is a claim about
 *    magnitude and players believe the claim; the slot-machine work on "losses
 *    disguised as wins" is the evidence that they believe it even when it is
 *    false. This one was making a jackpot's claim for a 2x.
 * 2. **Cartoon motion.** `show()` started `#group.scale` at `0.4 - 0.1k` and
 *    tweened it to `1.08 + 0.12k` on `back.out(2.2 + 0.9k)`. `back.out(s)`
 *    overshoots its own target by `4s^3 / 27(s+1)^2`, so the two composed:
 *
 *    | rung | start | tween target | ease overshoot | **peak** | tilt |
 *    |---|---|---|---|---|---|
 *    | 2x | 0.37 | 1.116 | 18.5% | **1.254** | -2.6 deg |
 *    | 3x | 0.34 | 1.150 | 21.6% | **1.324** | -3.2 deg |
 *    | 4x | 0.32 | 1.176 | 24.0% | **1.381** | -3.6 deg |
 *    | 5x | 0.31 | 1.192 | 25.5% | **1.417** | -3.9 deg |
 *    | 6x | 0.30 | 1.200 | 26.3% | **1.436** | -4.0 deg |
 *
 *    A double overshoot nobody would find by reading either line alone: a 6x
 *    started at 30% of size, tilted four degrees, punched to 144%, fell back and
 *    righted itself. Published premium practice caps a spring's first overshoot
 *    at a few percent and says never to start an entrance below 0.9.
 * 3. **The wrong material.** Bevel + gradient fill + stroke + specular sweep +
 *    starburst is the free-to-play vocabulary almost item for item. It is also
 *    the exact set of moves `@/core/tokens` forbids: gold there is "reserved,
 *    never decorative", and the plate spent it on a body gradient, a 3-6 px
 *    stroke, a bevel wash, a 1118-unit glow and up to 26 sparks.
 * 4. **It covered the cards.** In portrait the plate deliberately parked over
 *    the bottom pile row. In a card game the cards are the art direction.
 *
 * ## What it is now, in two layers
 *
 * 1. **A pool of shade.** An edge-free radial multiply under the type, darkening
 *    the felt so flat type can read against it. **A shape with an edge is a
 *    chip; a gradient with no edge is lighting.** It is the one thing kept from
 *    the old file's eight layers, repurposed: it used to add the ladder's colour
 *    and it now subtracts light.
 * 2. **The numeral, and the stake under it.** `type.display` at
 *    `CARD.height * 0.30`, tracked at 0.10 em, in a **flat** `ladderColour`.
 *    No stroke, no gradient fill, no drop shadow — all three are the material
 *    register above, and all three exist to separate type from its background by
 *    *outline* where a premium setting separates it by *lighting*. The stake
 *    line keeps `palette.gold`, because it is money, which is the one thing gold
 *    is for.
 *
 * The "STREAK" caption is gone: the HUD pill already says the word, and one
 * clear message per graphic. What the pill does not do is announce, which is the
 * only job left here.
 *
 * ## Escalation by rhythm and scope, not amplitude
 *
 * The old ladder escalated by turning dials on one channel: more rays (14 to
 * 24), more rings (1 to 3), more sparks (8 to 26), bigger overshoot (25% to
 * 44%). Five events differing only in dial position is the definition of "the
 * first one, louder", and habituation eats it by the third firing.
 *
 * **The numeral is the same size at 2x and at 6x.** What escalates is:
 *
 * - **Beat count.** Rung *n* fires *n* accents, `BEAT` apart. One at 2x, five at
 *   6x. A big event is *more beats in a row*, which is how Balatro reads big and
 *   how Halo's multikills read as a run.
 * - **Colour**, from the shared ladder — and this is the case `TARGET_LUMA` was
 *   computed for. A flat fill on a darkened field is exactly where a luminance
 *   ramp does visible work; against the old white-to-hue gradient, a white
 *   stroke and a bevel it did none.
 * - **Pool depth and hold**, both mildly.
 * - **Scope**, outside this file: the pill, the rim, the felt tint and the
 *   hit-stop all take a share, so a high rung has *more of the frame taking
 *   part* rather than one louder object.
 *
 * ## Why the beat is 75 ms and not the 90 ms the research proposed
 *
 * `@/audio/sfx`'s `streak()` is a two-note pip: the root, then a fifth **75 ms**
 * later, plus an octave at 150 ms at the top rung only. Those are the intervals
 * actually on the wire. 90 ms would have drifted against them by a frame per
 * beat and read as two systems that nearly agree, which is worse than either
 * agreeing or plainly not. At 75 ms the first two accents land on the two notes
 * every rung plays, and at 6x the third lands on the octave.
 *
 * ## The bank flight is kept
 *
 * The exit flies the type *up toward the HUD* while shrinking rather than fading
 * where it stands, because that direction says the points went to the counter.
 * It was the best idea in the old file and it is the same idea as Forza's skill
 * ring. The pool does not travel with it: the pool is light on the felt, and
 * light does not fly.
 *
 * Lives on the **overlay** layer, whose parallax ratio is 0: a callout over the
 * scene, not an object in it.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { gsap } from 'gsap';

import { radialFill } from '@/core/fills';
import { LADDER_MAX, ladderColour, mixRgb, momentum } from '@/vfx/streakLadder';
import { palette, type as typeTokens } from '@/core/tokens';
import { CARD, DESIGN, playBottom } from '@/pixi/layout';

/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

/* --------------------------------------------------------------- the type -- */

/**
 * Numeral size, in design units. 87 at the authored card size.
 *
 * Down from `CARD.height * 0.36` — not because the number should be small, but
 * because it no longer has to out-shout a plate, a bevel and a starburst to be
 * seen. The pool under it does that work with no area of its own.
 */
const SIZE_NUMERAL = CARD.height * 0.3;

/**
 * Tracking, in ems. Wider than `type.displayTracking`'s 0.06 em, which is set
 * for running display text; this is a two-glyph all-caps setting, and generous
 * letterspacing in caps is most of what makes a plain setting read as confident
 * rather than as unstyled.
 */
const TRACK_NUMERAL = 0.1;

/** The stake line, matching the pill's `.w21-streak__stake` in size and colour. */
const SIZE_STAKE = SIZE_NUMERAL * 0.35;
const TRACK_STAKE = 0.14;

/** Gap between the numeral's baseline box and the stake line. */
const STAKE_GAP = SIZE_STAKE * 0.55;

/* --------------------------------------------------------------- the pool -- */

/**
 * Reach of the shade pool, in design units — 455 at the authored card size.
 *
 * Compare the 766-unit ray fan and 559-unit glow it replaces. The pool is
 * bigger than the type it serves *on purpose*: a darkening that ends near the
 * glyphs has a visible boundary, and a visible boundary is a plate again. It
 * has to be wide enough that its own falloff is the only edge in it.
 */
const POOL_RADIUS = CARD.width * 2.2;

/** Centre opacity of the pool, floor and ceiling. Depth climbs with the rung. */
const POOL_ALPHA_MIN = 0.38;
const POOL_ALPHA_MAX = 0.55;

/* ------------------------------------------------------------- the timing -- */

/**
 * Arrival: 120 ms, scale 0.94 to 1.00, no overshoot and no tilt.
 *
 * `power4.out` rather than the `cubic-bezier(0.23, 1, 0.32, 1)` the reference
 * names: GSAP's quintic-out is the same family and the same shape to within a
 * couple of percent, and it needs no `CustomEase` plugin. It puts 63% of the
 * travel into the first fifth of the duration, so the arrival still reads as a
 * hit — what it does not do is come back past its final size.
 *
 * Weight in the real world means a thing does *not* bounce back. Overshoot
 * signals low mass, which is exactly why 44% of it read as a toy.
 */
const ARRIVE = 0.12;
const ARRIVE_SCALE = 0.94;

/**
 * Hold, seconds: long enough to read the number twice, and no longer.
 *
 * Broadcast practice for a lower third is "read the information twice before
 * pulling it off screen", which is 3-6 seconds for a viewer with nothing else to
 * do; scaled for a two-glyph numeral in a game it comes out here.
 *
 * The ceiling is set by the total. Arrival plus hold plus flight has to stay
 * under **1.15 s at the top rung**, because 1.15 s is what the old badge cost at
 * its *cheapest* — it ran 1.15 s at 2x to 1.43 s at 6x, five times a round, and
 * nothing in any published motion guidance contemplates a non-modal
 * notification holding that long that often. So the escalation in hold is real
 * but small: 0.72 s at 2x to 0.84 s at 6x, for 1.04 s to 1.16 s on screen.
 */
const HOLD_BASE = 0.72;
const HOLD_PER_RUNG = 0.03;

/** The bank flight. */
const BANK = 0.2;
/** How far the pool trails the type on the way out. */
const POOL_OUT = 0.14;

/** Seconds between accents. See the header: this is `sfx.streak`'s own interval. */
const BEAT = 0.075;
/** Scale and brightness of one accent. */
const BEAT_SCALE = 1.03;
const BEAT_LIFT = 0.4;
/**
 * Rise and fall of one accent, and they sum to exactly `BEAT` on purpose.
 *
 * Consecutive accents then chain instead of overlapping. An accent whose tail
 * runs past the next one's head leaves two tweens writing the same property in
 * the same frame, and which of them wins is a scheduling detail rather than a
 * design decision — the sort of thing that looks fine at 2x, where there is only
 * one accent, and goes wrong at 6x, where there are five.
 */
const BEAT_UP = 0.03;
const BEAT_DOWN = BEAT - BEAT_UP;

/** Labels for the ladder, matching the HUD. Level 1 is the 2x callout. */
const LABELS = ['2×', '3×', '4×', '5×', '6×'];

/* ------------------------------------------------------------- the plan -- */

/**
 * Everything a rung's callout does, as numbers.
 *
 * Split out from the display object for the same reason `StreakAuraState` is:
 * constructing a `Text` needs a canvas, so the class can only be exercised in a
 * browser, while the part worth protecting — that the entrance never overshoots,
 * that the beat count is the rung, that screen time stays inside the budget the
 * research set — is arithmetic that deserves a node test.
 *
 * It is also the honest answer to "what did this actually change", which is why
 * `peakScale` and `screenTime` are in here rather than being left implicit in a
 * timeline nobody can read off.
 */
export interface CalloutPlan {
  /** 1..5, i.e. the 2x..6x callouts. */
  rung: number;
  /** Flat fill for the numeral, from the shared ladder. */
  colour: number;
  /** Seconds of arrival, hold and bank flight. */
  arrive: number;
  hold: number;
  exit: number;
  /** Accents, one per rung, this many seconds apart. */
  beats: number;
  beatGap: number;
  /** Scale the arrival starts from, and the largest it ever reaches. */
  fromScale: number;
  /**
   * Peak scale as a multiple of final size, accents included.
   *
   * The number this rework exists to change. It used to be 1.254 at 2x and
   * **1.436 at 6x**, from a start scale of 0.30 and a 4-degree tilt.
   */
  peakScale: number;
  /** Centre opacity of the shade pool. */
  poolAlpha: number;
  /** Seconds on screen, first frame of the arrival to the last of the flight. */
  screenTime: number;
}

/**
 * The plan for a rung. Pure; the class below is the renderer for it.
 *
 * `momentum` is the front-loaded ladder intensity — see `@/vfx/streakLadder`
 * for why the reachable rungs carry most of the escalation — and it drives pool
 * depth and hold. Beat count is the rung itself, because the whole point of a
 * rhythmic escalation is that you can count it.
 */
export function calloutPlan(level: number): CalloutPlan {
  const rung = clampLevel(level);
  const k = momentum(rung);
  return {
    rung,
    colour: ladderColour(rung),
    arrive: ARRIVE,
    hold: HOLD_BASE + HOLD_PER_RUNG * (rung - 1),
    exit: BANK,
    beats: rung,
    beatGap: BEAT,
    fromScale: ARRIVE_SCALE,
    // The arrival lands exactly on 1. The only thing that ever exceeds it is an
    // accent, and only from the second one — the first rides the arrival.
    peakScale: rung > 1 ? BEAT_SCALE : 1,
    poolAlpha: POOL_ALPHA_MIN + (POOL_ALPHA_MAX - POOL_ALPHA_MIN) * k,
    screenTime: ARRIVE + HOLD_BASE + HOLD_PER_RUNG * (rung - 1) + BANK,
  };
}

/* ----------------------------------------------------------------- layout -- */

/**
 * Where the callout lands, in design units.
 *
 * Functions rather than constants because the design rect is per-orientation
 * (see `@/pixi/layout`), and this reads it at the moment the callout is placed.
 */
function homeX(): number {
  return DESIGN.width * 0.5;
}

/**
 * The foot of the table, in both orientations, by the same rule.
 *
 * The old placement had a per-orientation special case whose portrait branch put
 * the plate over the bottom row's cards and said so in its own comment. The rule
 * now is **never over the play, and never under the HUD**, which turns out to
 * leave exactly one band in each orientation.
 *
 * The obvious answer — the strip above the piles, where the plate sat in
 * landscape — does not survive contact with the DOM HUD, and this was worth
 * measuring rather than reasoning about. The HUD is *not* inside the design
 * rect: it is DOM laid over the canvas in screen pixels, and on a 1920x1080
 * window it occupies roughly the top 190 design units with the score, its label
 * and the streak pill. The topmost thing the *game* draws is a pile's total
 * badge, at `PILES.y - pileReachUp()`, which is 207 in landscape. Seventeen
 * units of clear band. A callout placed there lands on the score readout, which
 * a capture of the first attempt showed immediately.
 *
 * So it goes below the play instead:
 *
 * - `playBottom()` is the foot of a full five-card fan on the bottom row — 869
 *   in landscape, 1457 in portrait — and the callout clears it by a gutter.
 * - The floor is `0.92` of the design height, which keeps it off the UNDO and
 *   PAUSE controls in the bottom corners.
 * - Landscape is the tight one: the two constraints meet, so the callout is
 *   pinned to the floor and clears a *four*-card fan comfortably while a
 *   transient five-card fan — a pile that is about to clear anyway — may share
 *   its last few units, which are the bottom edges of cards and carry no index.
 *
 * There is a second, better reason to prefer the foot once the top is ruled out.
 * The bank flight now crosses the whole board on its way to the counter, which
 * is a far clearer statement of "the points went up there" than a 350-unit hop
 * was, and it is the same idea as Forza's skill ring filling.
 */
function homeY(): number {
  const half = boxHalfHeight();
  const wanted = playBottom() + CARD.height * 0.1 + half;
  const floor = DESIGN.height * 0.92 - half;
  return Math.min(wanted, floor);
}

/** Half the height of the type block, numeral and stake together. */
function boxHalfHeight(): number {
  return (SIZE_NUMERAL + STAKE_GAP + SIZE_STAKE) * 0.5;
}

/**
 * How far the bank flight travels, in design units.
 *
 * A distance rather than a destination. Flying all the way to `y = -0.06 * H`
 * from the foot of the table would be a thousand units in 180 ms, which is not
 * a bank, it is a rocket. What has to read is the *direction* and the shrink;
 * the type is transparent long before it would have arrived anywhere.
 */
const BANK_RISE = 0.2;

function clampLevel(level: number): number {
  const rounded = Number.isFinite(level) ? Math.round(level) : 1;
  return Math.min(Math.max(rounded, 1), LADDER_MAX);
}

function formatPoints(points: number): string {
  return points.toLocaleString('en-US');
}

export interface StreakBadgeOptions {
  reducedMotion?: boolean;
}

/**
 * One callout, reused. A new streak level replaces whatever is on screen rather
 * than stacking — two multipliers visible at once would be a lie about the game
 * state, and the newer one is always the true one.
 */
export class StreakBadge extends Container {
  readonly #pool: Graphics;
  readonly #group: Container;
  readonly #beat: Container;
  readonly #numeral: Text;
  readonly #stake: Text;

  #timeline: gsap.core.Timeline | null = null;
  #reduced: boolean;
  /** How far the numeral is lifted toward white, 0..1. See {@link StreakBadge.#accent}. */
  readonly #lift = { value: 0 };
  /** Centre opacity the pool was last painted at, so a beat can lift from it. */
  #poolAlpha = POOL_ALPHA_MAX;

  constructor(options: StreakBadgeOptions = {}) {
    super({ label: 'streak-badge' });
    this.eventMode = 'none';
    this.#reduced = options.reducedMotion ?? false;

    // Deliberately outside `#group`: the type banks toward the HUD on the way
    // out and the pool does not travel with it. Light stays on the felt.
    this.#pool = new Graphics({ blendMode: 'multiply', alpha: 0 });
    this.addChild(this.#pool);

    this.#group = new Container({ label: 'streak-callout' });
    this.addChild(this.#group);

    // A second container inside the first, holding nothing but the accent
    // pulse.
    //
    // Two transforms rather than one because the arrival and the accents
    // overlap in time and would otherwise be two tweens writing the same
    // `scale` in the same frame — the arrival runs to 120 ms and the second
    // accent starts at 75 ms, so which of them wins is a scheduling detail
    // rather than a design decision. It also looks correct at 2x, where there
    // is only one accent and no overlap, and goes wrong from 3x up. Nesting
    // them composes instead: the group carries the arrival and the bank
    // flight, this carries the beat, and the renderer multiplies.
    this.#beat = new Container({ label: 'streak-callout-beat' });
    this.#group.addChild(this.#beat);

    // Both texts are rasterised WHITE and coloured by `tint`.
    //
    // Not a shortcut — it is what makes the accents free. A `fill` change
    // re-rasterises the glyphs into a new texture, so a brightness kick per beat
    // would be five texture uploads on the loudest frame of the round. A tint is
    // a vertex colour. It also guarantees the colour is exactly
    // `ladderColour(rung)` with nothing composited over it.
    this.#numeral = new Text({
      text: '2×',
      style: new TextStyle({
        fontFamily: typeTokens.display,
        fontSize: SIZE_NUMERAL,
        letterSpacing: SIZE_NUMERAL * TRACK_NUMERAL,
        fill: palette.white,
        align: 'center',
      }),
    });
    this.#numeral.anchor.set(0.5);

    this.#stake = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: typeTokens.display,
        fontSize: SIZE_STAKE,
        letterSpacing: SIZE_STAKE * TRACK_STAKE,
        fill: palette.white,
        align: 'center',
      }),
    });
    this.#stake.anchor.set(0.5);

    this.#beat.addChild(this.#numeral, this.#stake);

    this.#layout();
    this.position.set(homeX(), homeY());
    this.visible = false;
  }

  setReducedMotion(reduced: boolean): void {
    this.#reduced = reduced;
  }

  /**
   * Announce a streak.
   *
   * @param level - 1..5, i.e. the 2x..6x callouts.
   * @param points - what the streak just paid, for the stake line.
   * @param delay - seconds to wait before the arrival. Used to put the callout a
   *   beat behind the pile burst that triggered it, so the two read as cause
   *   and effect rather than as one event.
   */
  show(level: number, points: number, delay = 0): void {
    const plan = calloutPlan(level);
    const rung = plan.rung;
    const hue = plan.colour;

    this.#numeral.text = LABELS[rung - 1] ?? `${rung + 1}×`;
    this.#stake.text = points > 0 ? `+${formatPoints(points)}` : '';
    this.#layout();

    this.#paint(plan.poolAlpha);

    this.#timeline?.kill();
    const wait = Math.max(delay, 0);

    // Hidden until the delay elapses, so a callout waiting for its beat is not
    // sitting on screen catching the eye a frame early.
    this.visible = wait <= 0;

    const hold = plan.hold;
    this.#reset(hue);

    if (this.#reduced) {
      // The reduced path is now *equivalent* rather than degraded: the whole
      // effect is a flat numeral appearing and going away, which is what the
      // full path is too, minus the accents and the flight.
      this.#timeline = gsap
        .timeline({
          delay: wait,
          onStart: () => {
            this.visible = true;
          },
          onComplete: () => this.#park(),
        })
        .to(this.#group, { alpha: 1, duration: 0.12 }, 0)
        .to(this.#pool, { alpha: 1, duration: 0.12 }, 0)
        .to([this.#group, this.#pool], { alpha: 0, duration: 0.2 }, `+=${hold}`);
      return;
    }

    const tl = gsap.timeline({
      delay: wait,
      onStart: () => {
        this.visible = true;
      },
      onComplete: () => this.#park(),
    });

    // --- arrival: 0.94 to 1.00, no overshoot, no tilt ----------------------
    tl.to(this.#group, { alpha: 1, duration: ARRIVE * 0.6, ease: 'power2.out' }, 0)
      .fromTo(
        this.#group.scale,
        { x: ARRIVE_SCALE, y: ARRIVE_SCALE },
        { x: 1, y: 1, duration: ARRIVE, ease: 'power4.out' },
        0,
      )
      // The pool arrives fractionally ahead of the type, so the felt is already
      // dark when the glyphs land on it rather than darkening under them.
      .fromTo(this.#pool, { alpha: 0 }, { alpha: 1, duration: ARRIVE * 0.8, ease: 'power2.out' }, 0);

    // --- the accents: rung n gets n of them --------------------------------
    for (let i = 0; i < plan.beats; i += 1) {
      this.#accent(tl, i * plan.beatGap, hue, i === 0);
    }

    // --- hold --------------------------------------------------------------
    // Nothing on screen should be perfectly still, but a callout this quiet does
    // not need to float: the pool breathes once and that is the whole idle.
    tl.to(
      this.#pool.scale,
      { x: 1.05, y: 1.05, duration: hold * 0.5, ease: 'sine.inOut', yoyo: true, repeat: 1 },
      ARRIVE,
    );

    // --- bank it: up toward the HUD, shrinking -----------------------------
    const out = ARRIVE + hold;
    tl.to(this.#group, { y: -DESIGN.height * BANK_RISE, duration: BANK, ease: 'power2.in' }, out)
      .to(this.#group.scale, { x: 0.82, y: 0.82, duration: BANK, ease: 'power2.in' }, '<')
      .to(this.#group, { alpha: 0, duration: BANK, ease: 'power2.in' }, '<')
      // The pool stays put and goes out; it is light on the felt, not luggage.
      .to(this.#pool, { alpha: 0, duration: POOL_OUT, ease: 'power2.in' }, out);

    this.#timeline = tl;
  }

  /** Take the callout off screen immediately. */
  clear(): void {
    this.#timeline?.kill();
    this.#timeline = null;
    this.#park();
  }

  /* ------------------------------------------------------------- internals -- */

  /**
   * One accent.
   *
   * Two channels, both on the type: a 3% scale pulse and a lift toward white.
   * Deliberately small — five of these in a row is the signal, and five *large*
   * ones in a row is the old file's problem with extra steps. The pool is left
   * out of it on purpose: it is the lighting, and lighting that flickers once
   * per beat is a strobe.
   *
   * The first accent rides the arrival, so it only carries the brightness — the
   * scale is already being driven from 0.94.
   *
   * ## Why the brightness goes through a scalar and not through `tint`
   *
   * Because tweening `tint` directly is wrong, and wrong in a way that only
   * shows up on the frames nobody screenshots. `tint` is a packed 0xRRGGBB
   * integer, and GSAP interpolates it as a *number*: the channels are not
   * independent, so the path between two colours runs through whatever byte
   * patterns lie between the two integers.
   *
   * The first build of this did exactly that and the 6x callout flashed **red**
   * halfway through every accent. Gold is 0xFFC53D = 16,762,685 and the top
   * rung's lavender is 0xECBAFF = 15,514,367; the midpoint integer is
   * 0xF64000 — a vermillion that appears in no palette in this game. It is the
   * carry between the green and blue bytes, and a still frame of the entrance
   * caught it immediately.
   *
   * So the tween runs on a plain 0..1 and `mixRgb` does the blend per frame,
   * channel by channel, which is what a colour ramp has to be.
   */
  #accent(tl: gsap.core.Timeline, at: number, hue: number, first: boolean): void {
    const write = (): void => {
      this.#numeral.tint = mixRgb(hue, palette.white, this.#lift.value);
    };

    tl.call(
      () => {
        this.#lift.value = BEAT_LIFT;
        write();
      },
      undefined,
      at,
    ).to(
      this.#lift,
      {
        value: 0,
        duration: BEAT,
        ease: 'power2.out',
        onUpdate: write,
        onComplete: () => {
          this.#numeral.tint = hue;
        },
      },
      at,
    );

    if (first) return;

    tl.to(this.#beat.scale, { x: BEAT_SCALE, y: BEAT_SCALE, duration: BEAT_UP, ease: 'power3.out' }, at)
      .to(this.#beat.scale, { x: 1, y: 1, duration: BEAT_DOWN, ease: 'power2.out' }, at + BEAT_UP);
  }

  /**
   * Stack the two lines and centre them optically.
   *
   * Two corrections a plate would have hidden and bare type does not:
   *
   * - **Tracking is trailing.** Pixi adds the letter-space *after* every glyph
   *   including the last, so an anchored-centre string sits half a track to the
   *   left of where it looks like it should. Nudging it back by half is the
   *   difference between centred and nearly centred, and on a two-glyph setting
   *   at 0.10 em that is 4.4 units — visible.
   * - **The block, not the numeral, is what centres.** The stake line hangs
   *   below the numeral, so centring the numeral on the home point leaves the
   *   pair bottom-heavy. The whole block is offset up by half the stake's share.
   */
  #layout(): void {
    const drop = (STAKE_GAP + SIZE_STAKE) * 0.5;
    this.#numeral.position.set(SIZE_NUMERAL * TRACK_NUMERAL * 0.5, -drop);
    this.#stake.position.set(
      SIZE_STAKE * TRACK_STAKE * 0.5,
      -drop + SIZE_NUMERAL * 0.5 + STAKE_GAP + SIZE_STAKE * 0.5,
    );
  }

  /**
   * Redraw the pool at this rung's depth.
   *
   * Cheap enough to do per show — this fires a handful of times a round, not per
   * frame. The stops are `palette.ink` throughout: a multiply pool tinted toward
   * the ladder colour would be a coloured wash, which is a plate made of light.
   * The only colour in this callout is in the glyphs.
   */
  #paint(alpha: number): void {
    this.#poolAlpha = alpha;

    this.#pool.clear();
    this.#pool.circle(0, 0, POOL_RADIUS).fill(
      radialFill([
        { offset: 0, color: palette.ink, alpha: this.#poolAlpha },
        // Squared-ish shoulder rather than a straight ramp: a linear falloff has
        // a visible band where its slope changes, and the whole argument for a
        // pool over a plate is that it has no edge anywhere.
        { offset: 0.42, color: palette.ink, alpha: this.#poolAlpha * 0.52 },
        { offset: 0.75, color: palette.ink, alpha: this.#poolAlpha * 0.14 },
        { offset: 1, color: palette.ink, alpha: 0 },
      ]),
    );
  }

  /** Put every layer back to its resting transform before a new show. */
  #reset(hue: number = ladderColour(1)): void {
    this.position.set(homeX(), homeY());
    this.#group.position.set(0, 0);
    this.#group.scale.set(1);
    this.#group.alpha = 0;
    this.#beat.scale.set(1);
    this.#pool.scale.set(1);
    this.#pool.alpha = 0;
    this.#lift.value = 0;
    this.#numeral.tint = hue;
    this.#stake.tint = palette.gold;
  }

  #park(): void {
    this.visible = false;
    this.#reset();
  }

  /**
   * Draw the callout once, invisibly, so the first streak is not the frame that
   * pays for it.
   *
   * The callout is `visible = false` until a streak opens, and an invisible
   * object costs nothing — which is the problem. Its two `Text` objects have not
   * been rasterised into a texture and the pool's gradient has not been
   * generated, so all of that lands at once on the frame a run first opens. That
   * is the loudest moment in the game and the worst possible place to spend
   * twenty milliseconds.
   *
   * Painted at a real rung rather than an empty one, so the gradient and the
   * glyphs it warms are the ones it will actually draw.
   */
  warm(): void {
    this.#numeral.text = LABELS[0] ?? '2×';
    this.#stake.text = '+000';
    this.#layout();
    this.#paint(calloutPlan(1).poolAlpha);
    this.#reset();
    this.visible = true;
    // Not zero: a fully transparent object can be skipped, and a skipped object
    // rasterises no text.
    this.alpha = WARM_ALPHA;
    this.#group.alpha = 1;
    this.#pool.alpha = 1;
  }

  /** Undo {@link warm}. */
  cool(): void {
    this.alpha = 1;
    this.#park();
  }

  override destroy(): void {
    this.#timeline?.kill();
    this.#timeline = null;
    gsap.killTweensOf([
      this,
      this.#group,
      this.#group.scale,
      this.#beat,
      this.#beat.scale,
      this.#pool,
      this.#pool.scale,
      this.#numeral,
      this.#stake,
      this.#lift,
    ]);
    super.destroy({ children: true });
  }
}
